import classNames from 'classnames/bind';
import { useVideoPlayer } from 'hooks/useVideoPlayer';
import { t } from 'i18next';
import ReactPlayer from 'react-player';

import React from 'react';

import { LoadingSpinner } from '@thinkific/chalk/LoadingSpinner';
import { PlayCircle } from '@thinkific/chalk/react-icons';
import { toast } from '@thinkific/chalk/react-providers';

import LoomPreview from 'components/AttachmentLinkInput/LoomPreview';

import { Attachment } from 'schema/Post/types';

import styles from './VideoPlayer.module.scss';

const cx = classNames.bind(styles);

// Constants
const PLAYER_DIMENSIONS = { width: '100%', height: '100%' };

interface VideoPreviewProps {
  attachment: Attachment;
}

const VideoPlayer = ({ attachment }: VideoPreviewProps) => {
  const { state, actions } = useVideoPlayer(attachment);

  // Early return if no valid attachment ID
  if (!attachment.id) return null;

  const { isPlaying, isLoading, videoUrl, loomId, thumbnailUrl, playerParams, isS3 } = state;

  const { handleThumbnailClick } = actions;

  // Reusable ReactPlayer component
  const renderReactPlayer = (src: string) => (
    <div className={cx('player-wrapper')}>
      <ReactPlayer
        className={cx('react-player')}
        onError={() =>
          toast({
            title: t(
              'components-VideoPlayer-video_player_error',
              'Unable to play video. Refresh to try again.'
            ),
            variant: 'danger',
          })
        }
        {...PLAYER_DIMENSIONS}
        src={src}
        controls
      />
    </div>
  );

  // Handle Loom videos
  if (loomId) {
    return <LoomPreview loomId={loomId} />;
  }

  // Handle direct video URL (S3 fallback during processing)
  if (isS3) {
    return renderReactPlayer(videoUrl);
  }
  // Handle thumbnail with play button
  if (thumbnailUrl && !isPlaying) {
    return (
      <div className={cx('thumbnail-wrapper')}>
        <button
          aria-label={isLoading ? 'Loading video' : 'Play video'}
          className={cx('thumbnail-button')}
          disabled={isLoading}
          type="button"
          onClick={handleThumbnailClick}
        >
          <div className={cx('icon-wrapper')}>
            {isLoading ? (
              <LoadingSpinner a11yLabel="Loading video" size="lg" onDark />
            ) : (
              <PlayCircle className={cx('play-circle')} color="onDark.icon.primary" filled />
            )}
          </div>
          <img
            alt={t('components-VideoPlayer-video_player_thumbnail_alt', 'Video thumbnail')}
            className={cx('thumbnail')}
            src={thumbnailUrl}
            onError={() =>
              toast({
                title: t(
                  'components-VideoPlayer-video_player_thumbnail_error',
                  'Preview image isn’t loading. Refresh to try again.'
                ),
                variant: 'danger',
              })
            }
          />
        </button>
      </div>
    );
  }

  // Handle embedded video player
  if (isPlaying && !isS3) {
    return (
      <div className={cx('player-wrapper')}>
        <iframe
          allow="autoplay; fullscreen; picture-in-picture"
          className={cx('player-wrapper__iframe')}
          src={`${attachment.url}?${playerParams}`}
          title={t('components-VideoPlayer-video_player_title', 'Video')}
          onError={() =>
            toast({
              title: t(
                'components-VideoPlayer-video_player_error',
                'Unable to play video. Refresh to try again.'
              ),
              variant: 'danger',
            })
          }
        />
      </div>
    );
  }

  // Fallback to ReactPlayer
  return renderReactPlayer(videoUrl);
};

export default VideoPlayer;
